import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Download, Copy, FileCode, Search, RefreshCw, AlertTriangle, GitCommitHorizontal } from "lucide-react";
import { toast } from "sonner";
import * as T from "./api";
import ErrorDetailsDialog from "./ErrorDetailsDialog";
import { MONO } from "./theme";

/**
 * Estimate Developer Centre — what code is deployed, what the AI changed,
 * and which errors occurred. Same access rule as the rest of Estimating.
 * Read-only; secrets never leave the server (redacted there).
 */

const SUBTABS = [["build", "Build"], ["source", "Source"], ["changes", "Changes"], ["diagnostics", "Diagnostics"]];

const KW = /\b(def|class|import|from|return|const|let|var|function|async|await|if|else|elif|for|while|try|except|catch|finally|new|export|default|null|undefined|None|True|False|true|false|raise|with|as|in|of|this|typeof)\b/;
const STR = /("[^"\n]*"|'[^'\n]*'|`[^`\n]*`)/;

/** Tiny syntax highlighter — comments, strings, keywords. */
function CodeLine({ line }) {
  const trimmed = line.trimStart();
  if (trimmed.startsWith("#") || trimmed.startsWith("//") || trimmed.startsWith("*")) {
    return <span className="text-emerald-500/80">{line}</span>;
  }
  const out = [];
  let rest = line;
  let k = 0;
  while (rest) {
    const sm = rest.match(STR);
    const km = rest.match(KW);
    const si = sm ? sm.index : Infinity;
    const ki = km ? km.index : Infinity;
    if (si === Infinity && ki === Infinity) { out.push(<span key={k++}>{rest}</span>); break; }
    if (si <= ki) {
      if (si > 0) out.push(<span key={k++}>{rest.slice(0, si)}</span>);
      out.push(<span key={k++} className="text-amber-300">{sm[0]}</span>);
      rest = rest.slice(si + sm[0].length);
    } else {
      if (ki > 0) out.push(<span key={k++}>{rest.slice(0, ki)}</span>);
      out.push(<span key={k++} className="text-sky-400">{km[0]}</span>);
      rest = rest.slice(ki + km[0].length);
    }
  }
  return <>{out}</>;
}

function PatchView({ patch }) {
  return (
    <pre className="max-h-[55vh] overflow-auto rounded-md bg-[#0b0b0f] p-3 text-[11px] leading-relaxed text-slate-300">
      {patch.split("\n").map((l, i) => (
        <div key={i} className={l.startsWith("+") ? "text-emerald-400" : l.startsWith("-") ? "text-red-400" : l.startsWith("@@") ? "text-amber-400" : ""}>
          {l || " "}
        </div>
      ))}
    </pre>
  );
}

export default function DevCentre() {
  const [sub, setSub] = useState("build");
  const [build, setBuild] = useState(null);
  const [tree, setTree] = useState(null);
  const [query, setQuery] = useState("");
  const [file, setFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diags, setDiags] = useState(null);
  const [diagDetail, setDiagDetail] = useState(null);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(async () => {
    try {
      const [b, t, h, d] = await Promise.all([T.devBuild(), T.devTree(), T.devHistory(), T.devDiagnostics()]);
      setBuild(b); setTree(t.files); setHistory(h); setDiags(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Developer Centre failed to load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openFile = async (path) => {
    setFileLoading(true);
    try { setFile(await T.devFile(path)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read that file"); }
    finally { setFileLoading(false); }
  };

  const openDiff = async (sha) => {
    setDiffLoading(true);
    try { setDiff(await T.devDiff(sha)); }
    catch { toast.error("Couldn't load that change"); }
    finally { setDiffLoading(false); }
  };

  const download = async (which) => {
    setDownloading(which);
    try { await T.devDownload(which); toast.success("Package downloaded"); }
    catch { toast.error("Download failed"); }
    finally { setDownloading(""); }
  };

  const filtered = useMemo(() => {
    if (!tree) return [];
    const q = query.trim().toLowerCase();
    return q ? tree.filter((f) => f.path.toLowerCase().includes(q)) : tree;
  }, [tree, query]);

  const copyFile = async () => {
    try { await navigator.clipboard.writeText(file.content); toast.success("File copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (!build) {
    return <div className="flex items-center gap-2 py-16 text-sm text-slate-500" data-testid="devcentre-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading Developer Centre…</div>;
  }

  return (
    <div className="space-y-4" data-testid="devcentre">
      {/* header + downloads */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {SUBTABS.map(([k, l]) => (
            <button key={k} onClick={() => setSub(k)} data-testid={`devcentre-sub-${k}`}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${sub === k ? "bg-[#f97316] text-white" : "bg-black/5 text-slate-500 hover:bg-black/10 dark:bg-white/10 dark:text-white/60"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => download("source.zip")} disabled={!!downloading} data-testid="devcentre-download-source"
            className="flex items-center gap-1.5 rounded-md bg-[#f97316] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#ea580c] disabled:opacity-60">
            {downloading === "source.zip" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download Deployed Source
          </button>
          <button onClick={() => download("debug.zip")} disabled={!!downloading} data-testid="devcentre-download-debug"
            className="flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-black/5 disabled:opacity-60 dark:border-white/20 dark:text-white/70">
            {downloading === "debug.zip" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download Debug Package
          </button>
        </div>
      </div>

      {sub === "build" && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="devcentre-build">
          {[
            ["Deployed commit", build.commit], ["Branch", build.branch], ["Deployed at", build.commitDate],
            ["Frontend build", build.swVersion], ["Backend", build.backendPython], ["Database", build.database],
            ["Storage", build.storage], ["Environment", build.environment], ["Source files", build.sourceFileCount],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-black/10 bg-white p-3 dark:border-white/10 dark:bg-white/[0.03]">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{k}</p>
              <p className="mt-0.5 break-all font-mono text-sm font-semibold text-slate-800 dark:text-white/90">{v ?? "—"}</p>
            </div>
          ))}
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 sm:col-span-2 lg:col-span-3">
            <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300" data-testid="devcentre-source-match">
              {build.sourceMatchesBuild
                ? `The source shown here is the live repository this build (${build.commit}) was deployed from — what you see is what is running.`
                : "Warning: displayed source may not match the deployed build."}
            </p>
            <p className={`mt-1 ${MONO} text-[11px] text-emerald-600/80 dark:text-emerald-400/70`}>Last change: {build.commitMessage}</p>
          </div>
        </div>
      )}

      {sub === "source" && (
        <div className="grid gap-3 lg:grid-cols-[300px_1fr]" data-testid="devcentre-source">
          <div className="overflow-hidden rounded-lg border border-black/10 dark:border-white/10">
            <div className="flex items-center gap-2 border-b border-black/10 bg-black/[0.03] px-2 py-1.5 dark:border-white/10 dark:bg-white/[0.04]">
              <Search className="h-3.5 w-3.5 text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search files…" data-testid="devcentre-source-search"
                className="w-full bg-transparent text-xs text-slate-700 focus:outline-none dark:text-white/80" />
            </div>
            <div className="max-h-[55vh] overflow-auto">
              {filtered.map((f) => (
                <button key={f.path} onClick={() => openFile(f.path)} data-testid={`devcentre-file-${f.path.replace(/[^a-z0-9]/gi, "-")}`}
                  className={`flex w-full items-center gap-1.5 px-2 py-1 text-left font-mono text-[11px] transition-colors ${file?.path === f.path ? "bg-[#f97316]/10 text-[#ea580c]" : "text-slate-600 hover:bg-black/5 dark:text-white/60 dark:hover:bg-white/5"}`}>
                  <FileCode className="h-3 w-3 shrink-0 opacity-50" />
                  <span className="truncate">{f.path}</span>
                </button>
              ))}
              {filtered.length === 0 && <p className="p-3 text-xs text-slate-400">No files match.</p>}
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-black/10 dark:border-white/10">
            {fileLoading ? (
              <div className="flex items-center gap-2 p-4 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading…</div>
            ) : !file ? (
              <p className="p-4 text-xs text-slate-400">Pick a file on the left. Read-only — secrets are stripped server-side.</p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 border-b border-black/10 bg-black/[0.03] px-3 py-1.5 dark:border-white/10 dark:bg-white/[0.04]">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-[11px] font-semibold text-slate-700 dark:text-white/80" data-testid="devcentre-file-path">{file.path}</p>
                    <p className={`${MONO} text-[10px] text-slate-400`}>
                      {file.size.toLocaleString()} bytes · modified {new Date(file.modified).toLocaleString()}
                      {file.lastCommit ? ` · last change: ${file.lastCommit}` : ""}
                    </p>
                  </div>
                  <button onClick={copyFile} data-testid="devcentre-copy-file" className="flex shrink-0 items-center gap-1 rounded border border-black/15 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-black/5 dark:border-white/20 dark:text-white/70">
                    <Copy className="h-3 w-3" /> Copy Entire File
                  </button>
                </div>
                <pre className="max-h-[55vh] overflow-auto bg-[#0b0b0f] p-3 text-[11px] leading-relaxed text-slate-300" data-testid="devcentre-file-content">
                  {file.content.split("\n").map((l, i) => (
                    <div key={i} className="flex">
                      <span className="w-9 shrink-0 select-none pr-3 text-right text-slate-600">{i + 1}</span>
                      <span className="whitespace-pre-wrap break-all"><CodeLine line={l} /></span>
                    </div>
                  ))}
                </pre>
              </>
            )}
          </div>
        </div>
      )}

      {sub === "changes" && (
        <div className="grid gap-3 lg:grid-cols-[320px_1fr]" data-testid="devcentre-changes">
          <div className="max-h-[60vh] overflow-auto rounded-lg border border-black/10 dark:border-white/10">
            {(history || []).map((h) => (
              <button key={h.commit} onClick={() => openDiff(h.commit)} data-testid={`devcentre-commit-${h.commit}`}
                className={`w-full border-b border-black/5 px-3 py-2 text-left transition-colors hover:bg-black/5 dark:border-white/5 dark:hover:bg-white/5 ${diff?.sha === h.commit ? "bg-[#f97316]/10" : ""}`}>
                <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-white/85">
                  <GitCommitHorizontal className="h-3.5 w-3.5 text-[#f97316]" /> {h.commit}
                  <span className="font-normal text-slate-400">{new Date(h.date).toLocaleString()}</span>
                </p>
                <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-500 dark:text-white/55">{h.message}</p>
                <p className={`mt-0.5 ${MONO} text-[10px] text-slate-400`}>{h.files.length} file(s): {h.files.slice(0, 3).map((f) => f.path.split("/").pop()).join(", ")}{h.files.length > 3 ? ` +${h.files.length - 3}` : ""}</p>
              </button>
            ))}
            {(history || []).length === 0 && <p className="p-3 text-xs text-slate-400">No change history available.</p>}
          </div>
          <div>
            {diffLoading ? (
              <div className="flex items-center gap-2 p-4 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading diff…</div>
            ) : !diff ? (
              <p className="p-4 text-xs text-slate-400">Pick a change to see exactly which files and lines it touched.</p>
            ) : (
              <>
                <p className={`mb-1 ${MONO} text-[11px] text-slate-500`}>commit {diff.sha}{diff.truncated ? " · truncated to 400 KB" : ""}</p>
                <PatchView patch={diff.patch} />
              </>
            )}
          </div>
        </div>
      )}

      {sub === "diagnostics" && (
        <div className="space-y-2" data-testid="devcentre-diagnostics">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Newest 30 upload/import/processing events. Correlation IDs match the backend logs exactly.</p>
            <button onClick={load} className="flex items-center gap-1 rounded border border-black/15 px-2 py-1 text-[10px] font-semibold text-slate-500 hover:bg-black/5 dark:border-white/20 dark:text-white/60" data-testid="devcentre-diag-refresh">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
          {(diags || []).length === 0 && (
            <p className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-300" data-testid="devcentre-diag-empty">
              No upload or import failures on record — everything recent completed cleanly.
            </p>
          )}
          {(diags || []).map((d, i) => (
            <button key={d.correlation_id + i} onClick={() => setDiagDetail(d)} data-testid={`devcentre-diag-${i}`}
              className="flex w-full items-center gap-3 rounded-lg border border-red-500/20 bg-red-500/[0.06] px-3 py-2 text-left hover:bg-red-500/10">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-slate-800 dark:text-white/85">{d.kind} / {d.stage} — {d.name || "unnamed"}</p>
                <p className={`truncate ${MONO} text-[10px] text-slate-400`}>{d.at} · id {d.correlation_id}</p>
              </div>
              <span className="shrink-0 rounded bg-red-500/15 px-1.5 py-0.5 font-mono text-[10px] font-bold text-red-600 dark:text-red-300">{d.http_status || "ERR"}</span>
            </button>
          ))}
          {diagDetail && (
            <ErrorDetailsDialog
              error={{
                title: diagDetail.kind, stage: diagDetail.stage, detail: diagDetail.detail,
                correlationId: diagDetail.correlation_id, name: diagDetail.name, size: diagDetail.size,
                received: diagDetail.received, offset: diagDetail.offset, chunk: diagDetail.chunk,
                httpStatus: diagDetail.http_status, lastStep: diagDetail.last_step,
              }}
              onClose={() => setDiagDetail(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
